import { run, acceptedCategory, showPreferences, setLanguage } from 'vanilla-cookieconsent'
import 'vanilla-cookieconsent/dist/cookieconsent.css'
import posthog, { posthogEnabled } from './posthog'
import type { App } from 'vue'

function syncPosthogConsent() {
  if (!posthogEnabled) return
  if (acceptedCategory('analytics')) {
    posthog.opt_in_capturing()
  } else {
    posthog.opt_out_capturing()
  }
}

const cookieConsentAPI = { showPreferences, setLanguage }

declare module 'vue' {
  interface ComponentCustomProperties {
    $CookieConsent: typeof cookieConsentAPI
  }
}

const config = {
  categories: {
    necessary: {
      enabled: true,
      readOnly: true,
      services: {
        keycloak: { label: 'Keycloak' },
        mapbox: { label: 'Mapbox' },
        navvis: { label: 'NavVis' },
        cookieconsent: { label: 'Cookie Consent' }
      }
    },
    analytics: {
      enabled: false,
      autoClear: { cookies: [{ name: /^ph_/ }] },
      services: {
        posthog: { label: 'PostHog' }
      }
    }
  },
  onConsent: syncPosthogConsent,
  onChange: syncPosthogConsent,
  language: {
    default: 'sv',
    autoDetect: 'browser' as const,
    translations: {
      sv: {
        consentModal: {
          title: 'Vi använder cookies!',
          description:
            'Plattformen använder nödvändiga cookies för att fungera korrekt, samt analyscookies för att förstå hur tjänsten används. Analyscookies aktiveras endast med ditt samtycke.',
          acceptAllBtn: 'Acceptera alla',
          acceptNecessaryBtn: 'Avvisa',
          showPreferencesBtn: 'Hantera inställningar'
        },
        preferencesModal: {
          title: 'Cookieinställningar',
          closeIconLabel: 'Stäng',
          acceptAllBtn: 'Acceptera alla',
          rejectAllBtn: 'Avvisa alla',
          savePreferencesBtn: 'Spara inställningar',
          sections: [
            {
              title: 'Nödvändiga cookies',
              description: 'Krävs för att plattformen ska fungera. Kan inte inaktiveras.',
              linkedCategory: 'necessary'
            },
            {
              title: 'Analyscookies',
              description:
                'Hjälper oss förstå funktionsanvändning, sessionslängd och eventuella fel. All data lagras på EU-servrar och delas inte med tredje part.',
              linkedCategory: 'analytics'
            }
          ]
        }
      },
      en: {
        consentModal: {
          title: 'We use cookies!',
          description:
            'Our platform uses essential cookies to ensure it works correctly, and analytics cookies to understand how you use it. Analytics cookies will only be enabled with your explicit consent.',
          acceptAllBtn: 'Accept all',
          acceptNecessaryBtn: 'Reject',
          showPreferencesBtn: 'Manage preferences'
        },
        preferencesModal: {
          title: 'Cookie preferences',
          closeIconLabel: 'Close',
          acceptAllBtn: 'Accept all',
          rejectAllBtn: 'Reject all',
          savePreferencesBtn: 'Save preferences',
          sections: [
            {
              title: 'Strictly necessary cookies',
              description: 'Required for the platform to function. Cannot be disabled.',
              linkedCategory: 'necessary'
            },
            {
              title: 'Analytics cookies',
              description:
                'Helps us understand feature usage, session duration, and errors. All data is hosted on EU servers and is not shared with third parties.',
              linkedCategory: 'analytics'
            }
          ]
        }
      }
    }
  }
}

export default {
  install: (app: App) => {
    app.config.globalProperties.$CookieConsent = cookieConsentAPI
    void run(config)
  }
}
